import { createPortal } from "react-dom";
import { X, BookOpen, Package, Check, ShoppingCart, ArrowRight, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import type { Book } from "@/components/BookCard";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { getFullUrl } from "@/lib/image-utils";
import { useBookImages } from "@/hooks/useBookImages";

interface BookDetailPanelProps {
  book: Book | null;
  onClose: () => void;
  onOpenCart: () => void;
}

const BookDetailPanel = ({ book, onClose, onOpenCart }: BookDetailPanelProps) => {
  const { addItem, items } = useCart();
  const navigate = useNavigate();
  const [justAdded, setJustAdded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  const { data: extraImages = [] } = useBookImages(book?.id);

  const images = useMemo(() => {
    if (!book) return [];
    const cover = book.cover_url ? [getFullUrl(book.cover_url) || book.cover_url] : [];
    return [...cover, ...extraImages];
  }, [book, extraImages]);

  if (!book) return null;

  const inCart = items.some((i) => i.product.id === book.id);
  const isOutOfStock = book.type === "physical" && book.stock_quantity !== null && book.stock_quantity <= 0;

  const handleAddToCart = () => {
    setAdding(true);
    addItem({
      id: book.id,
      title: book.title,
      price: book.price,
      cover_url: book.cover_url,
      type: "book",
      author: book.author,
    });
    // Brief delay to show feedback
    setTimeout(() => {
      setAdding(false);
      setJustAdded(true);
    }, 300);
  };
  const handleContinueShopping = () => { setJustAdded(false); onClose(); };
  const handleGoToCheckout = () => { setJustAdded(false); onClose(); onOpenCart(); };

  const prevImg = () => setActiveImg((p) => (p > 0 ? p - 1 : images.length - 1));
  const nextImg = () => setActiveImg((p) => (p < images.length - 1 ? p + 1 : 0));

  /* ── Shared image slider ── */
  const ImageSlider = ({ className = "", imgClass = "" }: { className?: string; imgClass?: string }) => (
    <div className={`relative flex items-center justify-center ${className}`}>
      {images.length > 0 ? (
        <img
          src={images[activeImg]}
          alt={book.title}
          width={400}
          height={600}
          loading="eager"
          decoding="async"
          onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
          className={`object-contain transition-opacity duration-300 ${imgClass}`}
          style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.35), -6px 6px 16px rgba(0,0,0,0.15)" }}
        />
      ) : (
        <div className="w-48 aspect-[2/3] rounded-xl flex items-center justify-center" style={{ background: "hsl(var(--primary)/0.15)" }}>
          {book.type === "ebook" ? <BookOpen className="w-14 h-14 text-primary/20" /> : <Package className="w-14 h-14 text-primary/20" />}
        </div>
      )}
      {images.length > 1 && (
        <>
          <button onClick={prevImg} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/60 backdrop-blur-sm flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-background/80 transition-colors">
            <ChevronLeft size={18} />
          </button>
          <button onClick={nextImg} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/60 backdrop-blur-sm flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-background/80 transition-colors">
            <ChevronRight size={18} />
          </button>
        </>
      )}
    </div>
  );

  /* ── Shared info block ── */
  const InfoBlock = ({ className = "" }: { className?: string }) => (
    <div className={`flex flex-col gap-4 ${className}`}>
      {book.is_preorder ? (
        <span className="self-start text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full bg-terracotta text-cream">
          Pre-order
        </span>
      ) : book.type !== "physical" && (
        <span className="text-xs font-semibold tracking-widest uppercase text-primary">
          {book.type === "ebook" ? "E. knyga" : "Audio knyga"}
        </span>
      )}

      <div>
        <h2 className="font-marquee text-2xl sm:text-3xl font-bold text-foreground leading-tight">{book.title}</h2>
        {book.author && <p className="text-sm text-muted-foreground mt-1">{book.author}</p>}
      </div>

      <p className="text-3xl font-bold text-foreground">
        €{book.price.toFixed(2)}
        {book.compare_at_price && book.compare_at_price > book.price && (
          <span className="ml-3 text-lg text-muted-foreground font-normal line-through">€{book.compare_at_price.toFixed(2)}</span>
        )}
      </p>

      {justAdded ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-primary"><Check size={16} /> Pridėta į krepšelį</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleContinueShopping} variant="outline" className="flex-1 gap-2 h-12 rounded-xl text-sm font-semibold">
              <ShoppingCart size={16} /> Tęsti apsipirkimą
            </Button>
            <Button onClick={handleGoToCheckout} className="flex-1 gap-2 h-12 rounded-xl text-sm font-semibold">
              Pereiti į apmokėjimą <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      ) : (
        <Button
          onClick={handleAddToCart}
          disabled={inCart || isOutOfStock || adding}
          className="w-full sm:w-auto gap-2 h-12 px-8 rounded-xl text-base font-semibold"
        >
          {adding ? (
            <><Loader2 size={18} className="animate-spin" /> Pridedama...</>
          ) : (
            <><ShoppingCart size={18} /> {inCart ? "Krepšelyje" : isOutOfStock ? "Nėra sandėlyje" : "Į krepšelį"}</>
          )}
        </Button>
      )}

      {book.description && (
        <div className="border-t border-border pt-4">
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{book.description}</p>
        </div>
      )}
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[9999]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md" />

      {/* ═══ MOBILE: full-screen scrollable page ═══ */}
      <div
        className="sm:hidden relative z-10 w-full h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "hsl(var(--background))" }}
      >
        <button onClick={onClose} className="fixed top-4 right-4 z-30 p-2.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors">
          <X className="w-5 h-5" />
        </button>

        <div className="w-full flex flex-col items-center bg-muted/50 px-8 pt-12 pb-6">
          <ImageSlider className="w-full max-w-[280px] aspect-[2/3]" imgClass="max-h-[420px] rounded-sm" />
          {images.length > 1 && (
            <p className="text-xs text-muted-foreground mt-4">{activeImg + 1} / {images.length}</p>
          )}
        </div>

        <InfoBlock className="px-6 py-6" />
      </div>

      {/* ═══ DESKTOP/TABLET: side-by-side modal ═══ */}
      <div
        className="hidden sm:flex relative z-10 items-center justify-center w-full h-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="relative w-full max-w-4xl rounded-2xl overflow-hidden flex flex-row"
          style={{
            background: "linear-gradient(160deg, hsl(var(--background)/0.98) 0%, hsl(var(--background)) 100%)",
            border: "1px solid hsl(var(--border))",
            boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
            maxHeight: "90vh",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>

          <div className="w-[45%] flex-shrink-0 relative overflow-hidden flex flex-col items-center justify-center" style={{ background: "hsl(var(--muted)/0.5)" }}>
            <div className="relative w-full h-full min-h-[500px] flex items-center justify-center">
              {images.length > 0 ? (
                <img src={images[activeImg]} alt={book.title} width={800} height={600} loading="eager" decoding="async" onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }} className="w-full h-full object-cover transition-opacity duration-300" />
              ) : (
                <div className="flex items-center justify-center w-full h-full">
                  {book.type === "ebook" ? <BookOpen className="w-16 h-16 text-primary/20" /> : <Package className="w-16 h-16 text-primary/20" />}
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={prevImg} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/70 backdrop-blur-sm flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-background transition-colors">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={nextImg} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-background/70 backdrop-blur-sm flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-background transition-colors">
                    <ChevronRight size={18} />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button key={i} onClick={() => setActiveImg(i)} className={`w-2 h-2 rounded-full transition-all ${i === activeImg ? "bg-white w-5" : "bg-white/40 hover:bg-white/60"}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="w-px bg-border" />

          <div className="flex-1 overflow-y-auto px-8 py-8">
            <InfoBlock />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BookDetailPanel;
